import React, { useCallback, useMemo } from 'react'
import { Row, Button, useContainerWidth, IconButton } from '@fs/zion-ui'
import { useQueryParams } from '@fs/zion-router'
import { DocumentRecordPerson, ContentAttach, DocumentStory, NoticeInfo } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { HelpOverlayButton } from '@fs/zion-help-overlay'
import { hasTranscript, useSlsStateContext, getImage } from '@fs/zion-sls'
import { getIndexExploreContextUrl } from '../../utils/utils'
import { useErrorStatus } from '../../../Lightyear/views/imageBrowse/hooks'
import { useOverlayContext } from '../../providers/overlayContextProvider/OverlayContext'
import { useR2DispatchContext, useR2StateContext } from '../../../RIP/providers/r2Provider/R2Context'
import { useSharedBrowserStateContext } from '../../providers/sharedBrowserProvider/SharedBrowserContext'

export default function NavButtons({ saveRecordOverlay, showNavButtons }) {
  const { t } = useTranslation()
  const atContainerWidth = useContainerWidth()
  const { query, setQuery } = useQueryParams({ replaceState: true })
  const { errorStatus } = useErrorStatus()
  const { infoSheetOverlay, closeAll } = useOverlayContext()
  const { initialView, currentView } = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const sharedBrowserState = useSharedBrowserStateContext()
  const contextUrl = getIndexExploreContextUrl()
  const zionSlsState = useSlsStateContext()
  const fullTextAttachFlowV2Flag = useFeatureFlag('rip_sharedImageViewer_fullTextAttachFlow_v2')

  const currentImage = useMemo(
    () => getImage(zionSlsState.images, zionSlsState.currentImageArk),
    [zionSlsState.images, zionSlsState.currentImageArk]
  )

  // Determine if the header is hidden (same logic as in RootBase.js)
  const isHeaderHidden = Boolean(contextUrl)
  // Always show help button when header is hidden
  const showHelpButton = isHeaderHidden

  const showTranscriptButtonV2 = useMemo(() => {
    return hasTranscript(currentImage?.elements)
  }, [currentImage])

  // when removing rip_sharedImageViewer_fullTextAttachFlow_v2, also remove the initialView and currentView logic, as this is only used for the fullText button logic
  const showTranscriptButton = useMemo(() => {
    return initialView === 'fullText' || currentView === 'fullText'
  }, [initialView, currentView])

  // Determine if the help button should be visible based on screen size and header visibility
  const isHelpButtonVisible = useMemo(() => {
    // Always show the help button when it's available, regardless of screen size
    // This ensures it's visible when the header is hidden
    return showHelpButton
  }, [showHelpButton])

  const isWideScreen = atContainerWidth({
    default: false, // Icon-only for smallest screens
    md: false, // Still icon-only > 600px
    lg: false, // Still icon-only > 750px
    xl: false, // Still icon-only > 950px
    xxl: true, // Text buttons only at xxl > 1200px
  })

  const handleViewChange = useCallback(
    (newView) => {
      setQuery({
        view: newView,
        personArk: undefined, // Reset personArk param when changing views
        ...(query.from && { from: query.from }),
      })

      // Close any open panels when changing views
      closeAll()

      // Remove the personArk from the state if the view is explore.
      if (newView === 'explore') {
        r2Dispatch({ type: 'SET_SELECTED_PERSON_ID', value: null })
      }

      const err = sharedBrowserState.imageError
      const imageStatusRestricted = err?.status !== 'OK' && err?.status !== 'UNAVAILABLE'
      if (!err || !imageStatusRestricted) {
        // Open the info sheet when changing views via nav buttons
        infoSheetOverlay.open()
      }
    },
    [setQuery, query.from, sharedBrowserState.imageError, r2Dispatch, infoSheetOverlay, closeAll]
  )

  // Only render on Lightyear's App, not R2/RIP
  if (!showNavButtons) return null

  if (isWideScreen) {
    return (
      <Row gutters="xs" alignY="middle">
        <Button
          noShrinkX
          color="gray"
          Icon={DocumentRecordPerson}
          onClick={() => handleViewChange('index')}
          data-testid="names-button"
          linkName="header-nav-names"
        >
          {t('headerNavigation.names', 'Names')}
        </Button>

        {fullTextAttachFlowV2Flag.isOn ? (
          showTranscriptButtonV2 && (
            <Button
              noShrinkX
              color="gray"
              Icon={DocumentStory}
              onClick={() => handleViewChange('fullText')}
              data-testid="transcript-button"
              linkName="header-nav-transcript"
            >
              {t('headerNavigation.transcript', 'Transcript')}
            </Button>
          )
        ) : (
          <>
            {showTranscriptButton && (
              <Button
                noShrinkX
                color="gray"
                Icon={DocumentStory}
                onClick={() => handleViewChange('fullText')}
                data-testid="transcript-button"
                linkName="header-nav-transcript"
              >
                {t('headerNavigation.transcript', 'Transcript')}
              </Button>
            )}
          </>
        )}

        <Button
          noShrinkX
          color="gray"
          Icon={NoticeInfo}
          onClick={() => handleViewChange('explore')}
          data-testid="information-button"
          linkName="header-nav-information"
        >
          {t('headerNavigation.information', 'Information')}
        </Button>

        <Button
          noShrinkX
          emphasis="high"
          data-testid="save-record-button"
          disabled={!!errorStatus}
          linkName="save-record"
          {...saveRecordOverlay.menuButtonProps}
        >
          {t('headerNavigation.saveRecord', 'Save Record')}
        </Button>

        {isHelpButtonVisible && <HelpOverlayButton data-testid="help-overlay-button" helpActionUrl="image-viewer" />}
      </Row>
    )
  }

  // For all other screen sizes, use icon buttons to save space
  return (
    <Row alignY="middle" gutters="xs">
      <IconButton
        title={t('headerNavigation.names', 'Names')}
        Icon={DocumentRecordPerson}
        onClick={() => handleViewChange('index')}
        data-testid="names-button"
        linkName="header-nav-names"
        color="gray"
      />

      {fullTextAttachFlowV2Flag.isOn
        ? showTranscriptButtonV2 && (
            <IconButton
              title={t('headerNavigation.transcript', 'Transcript')}
              Icon={DocumentStory}
              onClick={() => handleViewChange('fullText')}
              data-testid="transcript-button"
              linkName="header-nav-transcript"
              color="gray"
            />
          )
        : showTranscriptButton && (
            <IconButton
              title={t('headerNavigation.transcript', 'Transcript')}
              Icon={DocumentStory}
              onClick={() => handleViewChange('fullText')}
              data-testid="transcript-button"
              linkName="header-nav-transcript"
              color="gray"
            />
          )}

      <IconButton
        title={t('headerNavigation.information', 'Information')}
        Icon={NoticeInfo}
        onClick={() => handleViewChange('explore')}
        data-testid="information-button"
        linkName="header-nav-information"
        color="gray"
      />

      <IconButton
        noShrinkX
        title={t('headerNavigation.saveRecord', 'Save Record')}
        Icon={ContentAttach}
        data-testid="save-record-button"
        disabled={!!errorStatus}
        linkName="save-record"
        {...saveRecordOverlay.menuButtonProps}
      />

      {isHelpButtonVisible && <HelpOverlayButton data-testid="help-overlay-button" helpActionUrl="image-viewer" />}
    </Row>
  )
}
